import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { AworkDoc } from '@shared/types';

/**
 * Wählt die Anleitung zu einem Zugang aus awork aus.
 *
 * Getippt wird der Titel, gespeichert werden Titel und Adresse. Ist kein
 * awork-Schlüssel hinterlegt oder findet die Suche nichts, bleibt das Feld
 * für die Adresse von Hand bedienbar — eine Anleitung kann schließlich auch
 * woanders liegen.
 */
export function GuidePicker({
  title,
  url,
  onChange,
}: {
  title: string;
  url: string;
  onChange: (title: string, url: string) => void;
}): JSX.Element {
  const [suche, setSuche] = useState('');
  const [treffer, setTreffer] = useState<AworkDoc[]>([]);
  const [verfuegbar, setVerfuegbar] = useState<boolean | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [offen, setOffen] = useState(false);

  useEffect(() => {
    if (!offen) return;
    let aktuell = true;
    setLaedt(true);
    // Kurz warten, damit nicht jeder Tastendruck eine Anfrage auslöst.
    const timer = setTimeout(() => {
      api
        .aworkDocuments(suche)
        .then((r) => {
          if (!aktuell) return;
          setVerfuegbar(r.available);
          setTreffer(r.documents);
        })
        .catch(() => aktuell && setVerfuegbar(false))
        .finally(() => aktuell && setLaedt(false));
    }, 250);
    return () => {
      aktuell = false;
      clearTimeout(timer);
    };
  }, [suche, offen]);

  if (url && !offen) {
    return (
      <div className="guide-chosen">
        <div>
          <strong>{title || 'Anleitung'}</strong>
          <div className="small muted">{url.replace(/^https?:\/\//, '')}</div>
        </div>
        <div className="row-tight">
          <button type="button" className="btn-quiet btn-sm" onClick={() => setOffen(true)}>
            Ändern
          </button>
          <button type="button" className="btn-quiet btn-sm" onClick={() => onChange('', '')}>
            Entfernen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="stack">
      <input
        value={suche}
        placeholder="In awork suchen, z. B. „Fernzugriff“ …"
        onFocus={() => setOffen(true)}
        onChange={(e) => {
          setOffen(true);
          setSuche(e.target.value);
        }}
      />

      {offen ? (
        <div className="guide-results">
          {laedt && treffer.length === 0 ? <div className="small muted">Wird gesucht …</div> : null}
          {verfuegbar === false ? (
            <div className="small muted">
              Keine awork-Verbindung eingerichtet. Trage die Adresse unten von Hand ein.
            </div>
          ) : null}
          {verfuegbar && !laedt && treffer.length === 0 ? (
            <div className="small muted">Nichts gefunden.</div>
          ) : null}
          {treffer.map((doc) => (
            <button
              type="button"
              key={doc.id}
              className="guide-hit"
              onClick={() => {
                onChange(doc.name, doc.url);
                setOffen(false);
                setSuche('');
              }}
            >
              <strong>{doc.name}</strong>
              {doc.space ? <span className="small muted">{doc.space}</span> : null}
            </button>
          ))}
        </div>
      ) : null}

      <div className="field">
        <label htmlFor="f-guideurl">…oder Adresse von Hand</label>
        <input
          id="f-guideurl"
          value={url}
          onChange={(e) => onChange(title, e.target.value)}
          placeholder="https://wach.awork.com/docs/…"
        />
      </div>
    </div>
  );
}
